import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";
import { hasAccess } from "@/lib/roles";

export default function ChangePassword({ forced = false }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next === current) { toast.error("New password must differ from current"); return; }
    const { rulesPassed } = evaluatePassword(next);
    if (!rulesPassed) { toast.error("Password does not meet the strength requirements"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refresh();
      nav(hasAccess(user?.role, "admin") ? "/admin" : "/library");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password change failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-6" data-testid="change-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-full border-2 border-gold flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-gold" strokeWidth={1.5} />
          </div>
          <div className="eyebrow" data-testid="change-password-eyebrow">
            {forced ? "First-time login" : "Account security"}
          </div>
          <h1 className="aperture-h1 text-3xl mt-2">
            {forced ? "Please set a new password." : "Change your password."}
          </h1>
          {forced && <p className="mt-3 text-sm text-navy/60">You must change your temporary password before continuing.</p>}
        </div>
        <form onSubmit={submit} className="bg-white p-8 shadow-xl rounded-sm space-y-4">
          <div>
            <label className="eyebrow block mb-2">Current password</label>
            <input required type="password" value={current} onChange={(e)=>setCurrent(e.target.value)}
              data-testid="change-pw-current" className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
          </div>
          <div>
            <label className="eyebrow block mb-2">New password</label>
            <input required type="password" value={next} onChange={(e)=>setNext(e.target.value)}
              data-testid="change-pw-new" className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
            <PasswordStrength password={next} />
          </div>
          <div>
            <label className="eyebrow block mb-2">Confirm new password</label>
            <input required type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)}
              data-testid="change-pw-confirm" className="w-full px-4 py-3 border rounded-sm text-sm focus:outline-none focus:border-gold" style={{ borderColor: "rgba(10,37,64,0.15)" }} />
          </div>
          <button disabled={busy} data-testid="change-pw-submit" className="btn-primary w-full py-3 text-xs uppercase tracking-widest rounded-sm">
            {busy ? "Updating…" : "Update Password"}
          </button>
        </form>
        {user && <div className="mt-4 text-center text-xs text-navy/50">Signed in as {user.email}</div>}
      </div>
    </div>
  );
}
